import React from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useMediaQuery } from "usehooks-ts";

import { useChromeContext } from "@components/chrome/hooks";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { AttributionButton } from "@features/sales-v3/minimized-top-bar/components/attribution-button";
import { DateRangeSelect } from "@features/sales-v3/shared";
import { useFeatureFlags } from "@hooks/http";
import {
  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { AttributionModelSelect } from "@shared/selects";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { CompareDateRangeMenu } from "../compare-date-range-menu";
import { DateFilters } from "../date-filters";

import { SavedViewsMenu } from "./saved-views";

export const MinimizedTopBar = () => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { hasFeatureFlag } = useFeatureFlags();
  const { isSidebarExpanded } = useChromeContext();
  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      compareDateRange,
      dateRange,
      dateRangeInterval,
      level,
    },
    selectedSavedView,
    setAttributionModel,
    setAttributionWindow,
    setTimeGranularity,
    setDateRange,
    setCompareDateRange,
  } = useOverviewContext();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    level,
  };

  const isAtSupportedWidthForDateFilters = useMediaQuery("(max-width: 1100px)");
  const isAttributionWindowVisible = accountingMode === "accrual";
  const positionTop = hasFeatureFlag("enableDashboardV2") ? 76 : 56;
  const sidebarWidth = hasFeatureFlag("enableDashboardV2")
    ? isSidebarExpanded
      ? 240
      : 73
    : 56;

  return (
    <motion.div
      key="minimized-top-bar"
      initial={{ translateY: -positionTop }}
      exit={{ translateY: -positionTop }}
      animate={{ translateY: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "bg-v2-primary border-b border-v2-secondary py-1 px-2 flex items-center justify-between fixed z-30",
      )}
      style={{ top: positionTop, width: `calc(100% - ${sidebarWidth}px)` }}
    >
      <div className="flex items-center gap-1">
        <h3 className="text-lg font-bold m-0 max-w-xs truncate">
          {generateAnonBase64Hash({
            value: selectedSavedView?.name,
            prefix: "quick-filter",
            doAnon: isDemoMode && !selectedSavedView?.is_template,
          })}
        </h3>
        <SavedViewsMenu
          buttonClassName={clsx(
            "rounded-full border h-7 w-7 text-xs font-bold flex items-center justify-center",
          )}
          placement="bottom-start"
        />
        <div>
          <AttributionModelSelect
            menuClassName="w-[200px]"
            attributionModel={attributionModel}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel: value,
                  pageContext,
                },
              });

              setAttributionModel(value);
            }}
            label=""
            buttonRenderer={(props) => (
              <AttributionButton variant="combobox" label="Model" {...props} />
            )}
          />
        </div>
        {isAttributionWindowVisible && (
          <div>
            <AttributionWindowSelect
              menuClassName="w-[100px]"
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
              timeGranularity="daily"
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Minimized Top Bar",
                  action: "onSelect",
                  additionalActionInfo: "Set Attribution Window",
                  data: {
                    newAttributionWindow: value,
                    pageContext,
                  },
                });

                setAttributionWindow(value);
              }}
              label=""
              buttonRenderer={(props) => (
                <AttributionButton
                  variant="combobox"
                  label="Window"
                  {...props}
                />
              )}
            />
          </div>
        )}
        {isAtSupportedWidthForDateFilters ? (
          <DateRangeSelect
            timeGranularity={timeGranularity}
            dateRange={dateRange}
            hideHourlyOption={true}
            compareDateRange={compareDateRange}
            onTimeGranularityUpdate={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Time Granularity",
                data: {
                  newTimeGranularity: value,
                  pageContext,
                },
              });

              setTimeGranularity(value);
            }}
            onDateRangeUpdate={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Date Range",
                data: {
                  newDateRange: value,
                  pageContext,
                },
              });

              setDateRange(value);
            }}
            onCompareDateRangeUpdate={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Compare Date Range",
                data: {
                  newCompareDateRange: value,
                  pageContext,
                },
              });

              setCompareDateRange(value);
            }}
          />
        ) : (
          <>
            <TimeGranularityV2Select
              label=""
              className="text-xs"
              timeGranularity={timeGranularity}
              hideHourlyOption
              onChange={(value: ITimeGranularityV2) => {
                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onChange",
                  additionalActionInfo: "Set Time Granularity",
                  data: {
                    newTimeGranularity: value,
                    pageContext,
                  },
                });

                setDateRange(
                  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY[value],
                );
                setTimeGranularity(value);
              }}
              menuClassName="w-[100px]"
              width="fit-content"
              icon={null}
              buttonRenderer={(props) => (
                <AttributionButton variant="combobox" label="" {...props} />
              )}
            />
            <DateFilters className="h-7" isFromMinimizedTopBar />
            <CompareDateRangeMenu className="text-xs h-7" />
          </>
        )}
      </div>
    </motion.div>
  );
};
